import { validateGraph, type GraphValidationResult } from '../graph/engine.js';
import { toRenderSlot, type AppState } from '../state/appState.js';
import { DataProgramHandle, EventProgramHandle, QueryProgramHandle, RuntimeStateHandle } from 'fluveria-engine-wasm';

function makeEdgeKey(fromNode: string, fromPort: number, toNode: string, toPort: number): string {
  return `${fromNode}:${fromPort}:${toNode}:${toPort}`;
}

function computeInvalidEdgeKeysFor(appState: AppState, validation: GraphValidationResult): Set<string> {
  const invalid = new Set<string>();
  for (const issue of validation.issues) {
    if (issue.code !== 'type_mismatch' && issue.code !== 'dangling_edge') continue;
    for (const edge of appState.edges) {
      if (issue.nodeIds.includes(edge.fromNode) && issue.nodeIds.includes(edge.toNode)) {
        invalid.add(makeEdgeKey(edge.fromNode, edge.fromPort, edge.toNode, edge.toPort));
      }
    }
  }
  return invalid;
}

export interface EditorValidationState {
  validation: GraphValidationResult;
  invalidEdgeKeys: Set<string>;
}

export function computeValidationStateFor(appState: AppState): EditorValidationState {
  if (appState.editorGraphKind === 'data' || appState.editorGraphKind === 'query' || appState.editorGraphKind === 'event') {
    try {
      let handle: { free(): void };
      if (appState.editorGraphKind === 'data') {
        handle = new DataProgramHandle({ nodes: appState.nodes, edges: appState.edges });
      } else if (appState.editorGraphKind === 'query') {
        handle = new QueryProgramHandle({ nodes: appState.nodes, edges: appState.edges });
      } else {
        const state = new RuntimeStateHandle(appState.runtimeState);
        try {
          handle = new EventProgramHandle({ nodes: appState.nodes, edges: appState.edges }, state);
        } finally {
          state.free();
        }
      }
      handle.free();
      return { validation: { issues: [], nodeIssues: {} }, invalidEdgeKeys: new Set<string>() };
    } catch (error) {
      return {
        validation: {
          issues: [{ level: 'error', code: 'data_invalid', message: error instanceof Error ? error.message : String(error), nodeIds: [] }],
          nodeIssues: {},
        },
        invalidEdgeKeys: new Set<string>(),
      };
    }
  }
  if (appState.editorGraphSlot === 'material' && appState.nodes.length === 0 && appState.edges.length === 0) {
    return {
      validation: { issues: [], nodeIssues: {} },
      invalidEdgeKeys: new Set<string>(),
    };
  }
  const validation = validateGraph(appState.nodes, appState.edges, toRenderSlot(appState.editorGraphSlot));
  return {
    validation,
    invalidEdgeKeys: computeInvalidEdgeKeysFor(appState, validation),
  };
}

export function getValidationStateFor(appState: AppState): GraphValidationResult {
  return computeValidationStateFor(appState).validation;
}

export function getInvalidEdgeKeysFor(appState: AppState): Set<string> {
  return computeValidationStateFor(appState).invalidEdgeKeys;
}
